// Betterment Levy Data Collector
// Web scraping with 50% fallback approach

import config from '../config/api.js';

/**
 * Service to collect betterment levy rates via web scraping with 50% fallback
 */
class BettermentLevyService {
  
  /**
   * Get betterment levy rates for a municipality
   * @param {string} municipalityName - Municipality name
   * @returns {Promise<Object>} Levy rate data
   */
  static async getBettermentLevyRates(municipalityName) {
    try {
      console.log(`💰 Fetching betterment levy rates for: ${municipalityName}`);
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      // Try to get data from our web scraping API endpoint
      const response = await fetch(`${config.apiBaseUrl}/api/municipality`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'levy', municipalityName }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const levyData = await response.json();
        console.log(`✅ Betterment levy data retrieved for ${municipalityName}:`, {
          rate: levyData.tamaRate,
          source: levyData.dataSource
        });
        return levyData;
      } else {
        console.log(`⚠️ Betterment levy API unavailable, using 50% fallback`);
        return this.get50PercentFallback(municipalityName);
      }
      
    } catch (error) {
      console.warn(`❌ Error fetching betterment levy data for ${municipalityName}:`, error);
      return this.get50PercentFallback(municipalityName);
    }
  }
  
  /**
   * Get 50% fallback data with known exceptions
   * @param {string} municipalityName - Municipality name
   * @returns {Object} Fallback levy data
   */
  static get50PercentFallback(municipalityName) {
    // Known exceptions to 50% rule
    if (municipalityName === 'ירושלים') {
      return {
        municipality: municipalityName,
        tamaRate: 0.00,        // Jerusalem is exempt
        pinuyBinuyRate: 0.00,
        exemptions: ['Urban renewal projects fully exempt'],
        dataSource: 'known_exception',
        dataQuality: 'estimated_high_confidence',
        lastUpdated: new Date().toISOString(),
        notes: 'Jerusalem municipal policy - urban renewal exemption',
        confidence: 'high'
      };
    }
    
    // Default 50% for all other municipalities
    return {
      municipality: municipalityName,
      tamaRate: 0.50,          // 50% default
      pinuyBinuyRate: 0.50,    // 50% default
      exemptions: [],
      reductions: [],
      specialPrograms: [],
      dataSource: 'fallback_50_percent',
      dataQuality: 'estimated',
      lastUpdated: new Date().toISOString(),
      notes: 'Using 50% fallback rate - standard assumption for Israeli municipalities',
      confidence: 'medium'
    };
  }
  
  /**
   * Calculate betterment levy cost for a project
   * @param {number} valueIncrease - Property value increase in NIS
   * @param {string} municipalityName - Municipality name
   * @param {string} projectType - 'TAMA38' or 'PINUY_BINUY'
   * @returns {Promise<Object>} Levy calculation
   */
  static async calculateLevyCost(valueIncrease, municipalityName, projectType = 'TAMA38') {
    const levyData = await this.getBettermentLevyRates(municipalityName);
    
    const rate = projectType === 'TAMA38' ? levyData.tamaRate : levyData.pinuyBinuyRate;
    const levyCost = valueIncrease * rate;
    
    return {
      valueIncrease,
      levyRate: rate,
      levyCost,
      municipality: municipalityName,
      projectType,
      dataSource: levyData.dataSource,
      confidence: levyData.confidence,
      calculation: `₪${valueIncrease.toLocaleString()} × ${(rate * 100).toFixed(0)}% = ₪${levyCost.toLocaleString()}`
    };
  }
  
  /**
   * Get all supported municipalities with their expected rates
   * @returns {Object} Municipality rate expectations
   */
  static getSupportedMunicipalities() {
    return {
      'תל אביב-יפו': { expectedRate: 0.50, scrapingTarget: 'https://www.tel-aviv.gov.il' },
      'ירושלים': { expectedRate: 0.00, scrapingTarget: 'https://www.jerusalem.muni.il' },
      'גבעתיים': { expectedRate: 0.50, scrapingTarget: 'https://www.givataim.muni.il' },
      'רמת גן': { expectedRate: 0.50, scrapingTarget: 'https://www.ramat-gan.muni.il' },
      'חולון': { expectedRate: 0.50, scrapingTarget: 'https://www.holon.muni.il' },
      'בת ים': { expectedRate: 0.50, scrapingTarget: 'https://www.bat-yam.muni.il' },
      'פתח תקווה': { expectedRate: 0.50, scrapingTarget: null },
      'ראשון לציון': { expectedRate: 0.50, scrapingTarget: null },
      'הרצליה': { expectedRate: 0.50, scrapingTarget: null }
    };
  }
  
  /**
   * Validate levy rate data
   * @param {Object} levyData - Levy rate data to validate
   * @returns {Object} Validation results
   */
  static validateLevyData(levyData) {
    const validation = {
      isValid: true,
      warnings: [],
      errors: []
    };
    
    // Check rate ranges
    if (levyData.tamaRate < 0 || levyData.tamaRate > 1) {
      validation.errors.push('TAMA rate must be between 0% and 100%');
      validation.isValid = false;
    }
    
    if (levyData.tamaRate > 0.6) {
      validation.warnings.push('TAMA rate above 60% is unusually high');
    }
    
    // Check if using fallback
    if (levyData.dataSource === 'fallback_50_percent') {
      validation.warnings.push('Using 50% fallback rate - actual rate may vary');
    }
    
    return validation;
  }
}

export default BettermentLevyService;
